import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Loader2, ImageIcon, Crop } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { uploadImage } from "@/lib/api";
import { imgSrc, imgPos, imgZoom } from "@/lib/img";
import { ImagePositioner } from "./ImagePositioner";

export const ImageUploader = ({ label, value, onChange, testid, compact = false, positionable = false, aspectClass = "aspect-[4/3]" }) => {
  const [uploading, setUploading] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const inputRef = useRef(null);

  const url = imgSrc(value);
  const position = imgPos(value);
  const zoom = imgZoom(value);

  // Emit the right shape: an object when positioning is enabled, else a plain URL string.
  const emitUrl = (newUrl) => onChange(positionable ? { url: newUrl, position, zoom } : newUrl);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadImage(file);
      // Fresh upload resets position/zoom to center so the new photo shows fully.
      onChange(positionable ? { url: uploaded, position: "50% 50%", zoom: 1 } : uploaded);
      if (positionable) setShowAdjust(true); // auto-open the adjuster right after an upload
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      {label && <Label className="text-xs">{label}</Label>}
      <div className={`mt-1 flex gap-3 ${compact ? "items-center" : "items-start"}`}>
        <div className="w-16 h-16 rounded-lg overflow-hidden border border-[var(--border)] shrink-0 flex items-center justify-center bg-[var(--surface)]">
          {url ? (
            <img src={url} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-5 h-5 text-[var(--text-muted)]" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <Input value={url} onChange={(e) => emitUrl(e.target.value)} placeholder="Image URL or upload →" />
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testid}-file`} />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="text-xs rounded-lg px-3 py-1.5 border border-[var(--border-gold)] text-[var(--gold-hover)] flex items-center gap-1.5 hover:bg-[var(--rose)] transition-colors disabled:opacity-60"
              data-testid={`${testid}-upload-btn`}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Uploading..." : "Upload photo"}
            </button>
            {positionable && url && (
              <button
                type="button"
                onClick={() => setShowAdjust((s) => !s)}
                className="text-xs rounded-lg px-3 py-1.5 border border-[var(--border)] text-[var(--text-secondary)] flex items-center gap-1.5 hover:border-[var(--border-gold)] transition-colors"
                data-testid={`${testid}-adjust-btn`}
              >
                <Crop className="w-3.5 h-3.5" /> {showAdjust ? "Done adjusting" : "Adjust position & zoom"}
              </button>
            )}
          </div>
        </div>
      </div>
      {positionable && url && showAdjust && (
        <div className="mt-2">
          <ImagePositioner
            image={url}
            position={position}
            zoom={zoom}
            onChange={({ position: p, zoom: z }) => onChange({ url, position: p, zoom: z })}
            aspectClass={aspectClass}
            testid={`${testid}-positioner`}
          />
        </div>
      )}
    </div>
  );
};
